import { CSSProperties, FC, useMemo } from 'react';
import type { Content } from '@prismicio/client';
import classNames from 'classnames';

import { Container } from '@/components/Container';
import { Slider } from '@/components/Slider/Slider';
import {
  useFilteredSliceItemsByLocationSlug,
  useShowByLocationSlug,
} from '@/hooks';
import { getSlicePadding } from '@/utils';

import {
  AutoplayMode,
  ContentMarqueeCarouselContinuous,
  getSliderMotion,
  MarqueeSlide,
} from './ContentMarqueeCarousel.components';

import styles from './ContentMarqueeCarousel.module.scss';

export type ContentMarqueeCarouselComponentProps = {
  slice: Content.ContentMarqueeCarouselSlice;
  'data-slice-type'?: string;
  'data-slice-variation'?: string;
};

const ContentMarqueeCarousel: FC<ContentMarqueeCarouselComponentProps> = ({
  slice,
  'data-slice-type': dataSliceType,
  'data-slice-variation': dataSliceVariation,
}) => {
  const { showContent } = useShowByLocationSlug(slice?.primary?.location_slug);
  const primary = slice?.primary;

  const { filteredItems } = useFilteredSliceItemsByLocationSlug(slice?.items);

  const { activeAutoplay, autoplay, continuousMarquee, freeMode, loop, speed } =
    getSliderMotion(
      primary?.autoplay_mode as AutoplayMode | null | undefined,
      !!primary?.disable_autoplay_on_interaction,
    );

  const background = primary?.background_color;
  const backgroundImage = primary?.background_image;
  const backgroundStyleFixed = !!primary?.background_style_fixed_cover;
  const minHeight = primary?.min_heigth;
  const marginBottom = primary?.bottom_spacing;
  const fullWidth = primary?.full_width;
  const paddingSection = primary?.padding_section;
  const labelColor = primary?.item_label_color;
  const borderBottomColor = primary?.border_bottom_color;
  const borderBottomWidth = primary?.border_bottom_width;
  const borderInsideContainer = !!primary?.border_section_inside_container;
  const desktopHeightPx = primary?.item_height_desktop;
  const mobileHeightPx = primary?.item_height_mobile ?? desktopHeightPx;
  const constrainImageToSlot =
    primary?.item_height_mobile != null || primary?.item_height_desktop != null;

  const gapMobile = primary?.item_spacing_between ?? 8;
  const gapDesktop = primary?.item_spacing_between_desktop ?? 16;

  const breakpoints = useMemo(
    () => ({
      320: { spaceBetween: gapMobile },
      1024: { spaceBetween: gapDesktop },
    }),
    [gapDesktop, gapMobile],
  );

  const isBorderBottom = Boolean(borderBottomColor && borderBottomWidth);

  if (!primary?.active || filteredItems.length === 0 || !showContent) {
    return null;
  }

  // Per-breakpoint slot heights resolved in JS so desktop viewport always gets a height when
  // mobile (or shared fallback) is set—avoids relying on nested var() when desktop field is empty.
  const itemHeightSlotSm =
    mobileHeightPx != null ? `${mobileHeightPx}px` : undefined;
  const itemHeightSlotLg =
    desktopHeightPx != null ? `${desktopHeightPx}px` : itemHeightSlotSm;

  const sectionStyle = {
    '--cmc-item-h-sm': itemHeightSlotSm,
    '--cmc-item-h-lg': itemHeightSlotLg,
    '--cmc-label-color': labelColor ?? undefined,
    background: background ?? undefined,
    backgroundImage: backgroundImage?.url
      ? `url(${backgroundImage.url})`
      : undefined,
    borderBottom:
      isBorderBottom && !borderInsideContainer
        ? `${borderBottomWidth}px solid ${borderBottomColor}`
        : undefined,
    minHeight:
      minHeight != null && minHeight > 0 ? `${minHeight}vh` : undefined,
  } as CSSProperties;

  const containerStyle = {
    borderBottom:
      isBorderBottom && borderInsideContainer
        ? `${borderBottomWidth}px solid ${borderBottomColor}`
        : undefined,
  } as CSSProperties;

  const cssMarqueeTrackStyle = {
    '--cmc-marquee-duration': `${Math.min(
      100,
      Math.max(22, Math.round(speed / 55)),
    )}s`,
    '--cmc-marquee-gap-desktop': `${gapDesktop}px`,
    '--cmc-marquee-gap-mobile': `${gapMobile}px`,
  } as CSSProperties;

  const sectionAriaLabel = 'Content marquee carousel';

  return (
    <section
      aria-label={sectionAriaLabel}
      className={classNames(
        styles.content_marquee,
        getSlicePadding(paddingSection ?? 'large'),
        {
          ['background__fixed_cover']: backgroundStyleFixed,
          ['background__contained']: !backgroundStyleFixed,
          spacing_bottom: marginBottom,
        },
      )}
      data-slice-type={dataSliceType}
      data-slice-variation={dataSliceVariation}
      style={sectionStyle}
    >
      <Container
        className={classNames({
          [styles['content_marquee__container--full-width']]: fullWidth,
          [styles.content_marquee__container_with_border_spacing]:
            borderInsideContainer,
        })}
        style={containerStyle}
      >
        {continuousMarquee ? (
          <ContentMarqueeCarouselContinuous
            constrainImageToSlot={constrainImageToSlot}
            disableAutoplayOnInteraction={
              !!primary?.disable_autoplay_on_interaction
            }
            items={filteredItems}
            trackStyle={cssMarqueeTrackStyle}
          />
        ) : (
          <Slider
            activeAutoplay={activeAutoplay}
            ariaLabel={sectionAriaLabel}
            autoplay={autoplay}
            breakpoints={breakpoints}
            containerClassname={styles.content_marquee__track}
            freeMode={freeMode}
            loop={loop}
            showSliderHeader={false}
            showSlidesOutsideContainer
            spaceBetween={gapMobile}
            speed={speed}
            watchOverflow={false}
          >
            {filteredItems?.map((item, index) => (
              <MarqueeSlide
                constrainImageToSlot={constrainImageToSlot}
                item={item}
                key={`cmc-${index}-${item?.label ?? ''}-${
                  item?.internal_link ?? ''
                }`}
              />
            ))}
          </Slider>
        )}
      </Container>
    </section>
  );
};

export { ContentMarqueeCarousel };
